import { useState } from 'react'
import { useTranslation } from 'react-i18next'
import { ArrowDownTrayIcon } from '@heroicons/react/24/outline'
import {
  useApplyCarryOver,
  useCarryOverOffers,
} from '../../hooks/useTrainingLocations'

/**
 * VH-126 Stage C2: bring exercises from a gym you have already curated.
 *
 * A newly scanned location starts at zero exercises even when it is fitted with
 * the same machines as one you spent months curating. Re-authoring twenty
 * exercises by hand is the work that makes a feature get abandoned.
 *
 * Opt-in and explicit: nothing is cloned until the user picks it. Everything
 * starts unselected, because a bulk write that happened because the user did
 * not notice a pre-ticked box is the kind of thing you only discover later.
 */
export default function CarryOverPanel({ locationId }) {
  const { t } = useTranslation()
  const { data, isLoading } = useCarryOverOffers(locationId)
  const apply = useApplyCarryOver(locationId)
  const [selected, setSelected] = useState(() => new Set())
  const [done, setDone] = useState(null)

  if (isLoading || !data) return null

  const offers = data.offers || []
  if (offers.length === 0) {
    // Not an error and not worth a heading — a brand-new gym with no matches
    // anywhere simply has nothing to carry over, and Stage D is the route.
    return null
  }

  const toggle = (id) => {
    setSelected((prev) => {
      const next = new Set(prev)
      if (next.has(id)) next.delete(id)
      else next.add(id)
      return next
    })
  }

  const toggleOffer = (offer) => {
    const ids = offer.exercises.map((e) => e.id)
    const allOn = ids.every((id) => selected.has(id))
    setSelected((prev) => {
      const next = new Set(prev)
      ids.forEach((id) => (allOn ? next.delete(id) : next.add(id)))
      return next
    })
  }

  const submit = async () => {
    const result = await apply.mutateAsync([...selected])
    setDone(result.created_count)
    setSelected(new Set())
  }

  return (
    <section className="mt-8" data-testid="carry-over-panel">
      <h2 className="mb-1 text-xl font-semibold text-gray-900">
        {t('trainingLocations.carryOver.title')}
      </h2>
      <p className="mb-3 text-sm text-gray-600">
        {t('trainingLocations.carryOver.intro', { count: data.total_exercises })}
      </p>

      {done !== null && (
        <p
          className="mb-3 text-sm text-green-700"
          role="status"
          data-testid="carry-over-done"
        >
          {t('trainingLocations.carryOver.done', { count: done })}
        </p>
      )}

      {apply.isError && (
        <p className="mb-3 text-sm text-red-600" role="alert" data-testid="carry-over-error">
          {t('trainingLocations.carryOver.failed')}
        </p>
      )}

      <div className="space-y-4">
        {offers.map((offer) => (
          <div
            key={`${offer.equipment_id}-${offer.source_equipment_id}`}
            className="rounded-lg border border-gray-200 bg-surface-container p-4"
            data-testid={`carry-over-offer-${offer.equipment_id}`}
          >
            <div className="mb-2 flex items-center justify-between gap-3">
              <div className="min-w-0">
                <p className="truncate font-medium text-gray-900">
                  {offer.equipment_name}
                </p>
                {/* Where these came from matters: the same machine can match
                    twins at several places, and the user needs to know which
                    gym's version they are taking. */}
                <p className="text-xs text-gray-500">
                  {t('trainingLocations.carryOver.from', {
                    location: offer.source_location_name,
                  })}
                </p>
              </div>
              <button
                type="button"
                onClick={() => toggleOffer(offer)}
                className="shrink-0 text-sm text-primary-600 hover:text-primary-700"
                data-testid={`carry-over-toggle-${offer.equipment_id}`}
              >
                {t('trainingLocations.carryOver.selectAll')}
              </button>
            </div>

            <ul className="space-y-1">
              {offer.exercises.map((exercise) => (
                <li key={exercise.id}>
                  <label className="flex cursor-pointer items-center gap-2 text-sm text-gray-700">
                    <input
                      type="checkbox"
                      checked={selected.has(exercise.id)}
                      onChange={() => toggle(exercise.id)}
                      className="text-primary-600 focus:ring-primary-500"
                      data-testid={`carry-over-exercise-${exercise.id}`}
                    />
                    <span>{exercise.name_en}</span>
                  </label>
                </li>
              ))}
            </ul>
          </div>
        ))}
      </div>

      <button
        type="button"
        onClick={submit}
        disabled={selected.size === 0 || apply.isPending}
        className="btn-primary mt-4 flex items-center disabled:opacity-50"
        data-testid="carry-over-apply"
      >
        <ArrowDownTrayIcon className="mr-2 h-5 w-5" />
        {apply.isPending
          ? t('trainingLocations.carryOver.copying')
          : t('trainingLocations.carryOver.apply', { count: selected.size })}
      </button>
    </section>
  )
}
